import { Select } from 'antd';
import { type Dispatch, type SetStateAction, useMemo, useState } from 'react';

import { Badge } from '@/components/Badge/Badge';
import Modal from '@/components/Modal/Modal';
import useModalState from '@/hooks/pure/useModalState';
import useStates from '@/hooks/pure/useStates';
import { getCurrWorkspaceAlias } from '@/utils/workspace';

import { productInfoService } from '../api';
import { BaseTable, type RowData, type TableColumnT } from '../components/BaseTable';
import { ProductManagerEllipsisMenu } from '../components/DeleteItem/ProductManagerEllipsisMenu';
import { DetailModal } from '../components/DetailModal/DetailModal';
import { EllipsisCell } from '../components/EllipsisCell/EllipsisCell';
import { ListHeader } from '../components/ListHeader';
import { MergeModal } from '../components/MergeModal';
import useProductStore from '../store/ProductStore';
import type { ProductT } from '../types';

export const ProductView = () => {
  const fetchProductList = useProductStore((state) => state.fetchProductList);
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const productMap = useProductStore((state) => state.groupedProductMap);
  const productList = useMemo(
    () => Array.from(productMap.values()),
    [productMap],
  );

  const brandOptions = useMemo(() => {
    return Array.from(brandMap.values()).map((brand) => ({
      label: brand.name,
      value: brand.id,
    }));
  }, [brandMap]);

  const lineOptions = useMemo(() => {
    return Array.from(lineMap.values()).map((line) => ({
      label: line.name,
      value: line.id,
    }));
  }, [lineMap]);

  const [checkedList, setCheckedList] = useState<string[]>([]);
  const mergeModal = useModalState();

  const { state, setValue } = useStates<{
    searchValue: string;
    brandFilter: string[];
    lineFilter: string[];
  }>({
    searchValue: '',
    brandFilter: [],
    lineFilter: [],
  });

  const filteredProductList = useMemo(() => {
    return productList.filter((product) => {
      if (
        state.brandFilter.length > 0 &&
        product.brand_id &&
        !state.brandFilter.includes(product.brand_id)
      ) {
        return false;
      }
      if (
        state.lineFilter.length > 0 &&
        product.line_id &&
        !state.lineFilter.includes(product.line_id)
      ) {
        return false;
      }
      if (
        state.searchValue &&
        !product.product_name
          ?.toLowerCase()
          .includes(state.searchValue.toLowerCase())
      ) {
        return false;
      }
      return true;
    });
  }, [productList, state.brandFilter, state.lineFilter, state.searchValue]);

  return (
    <div className="flex flex-col gap-4 flex-1 overflow-auto">
      <ListHeader
        mergeDisabled={checkedList.length < 2}
        onSearchSubmit={(value) => {
          setValue('searchValue', value);
        }}
        clearSearch={() => {
          setValue('searchValue', '');
        }}
        onMergeClick={() => {
          mergeModal.open();
        }}
      >
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          className="min-w-40 max-w-100"
          options={brandOptions}
          placeholder="Select brand"
          onChange={(values) => {
            setValue('brandFilter', values);
          }}
          allowClear
        />
        <Select
          mode="multiple"
          showSearch
          optionFilterProp="label"
          className="min-w-40 max-w-100"
          options={lineOptions}
          placeholder="Select line"
          onChange={(values) => {
            setValue('lineFilter', values);
          }}
          allowClear
        />
        {checkedList.length > 0 && (
          <span className="text-sm text-gray-600">
            {checkedList.length} selected
          </span>
        )}
      </ListHeader>
      <ProductTable
        searchValue={state.searchValue}
        productList={filteredProductList}
        checkedList={checkedList}
        setCheckedList={setCheckedList}
      />
      <Modal status={mergeModal.status} closeModal={mergeModal.close}>
        <MergeModal
          items={checkedList.map((id) => ({
            id,
            name: productMap.get(id)?.product_name || '-',
          }))}
          target="product"
          mergeCallback={async (targetId, list) => {
            await productInfoService.mergeProducts({
              from: list.filter((id) => id !== targetId),
              to: targetId,
            });
          }}
          onDoneCallback={() => {
            setCheckedList([]);
            fetchProductList(getCurrWorkspaceAlias());
          }}
        />
      </Modal>
    </div>
  );
};

const ProductTable = (props: {
  productList: ProductT[];
  checkedList: string[];
  setCheckedList: Dispatch<SetStateAction<string[]>>;
  searchValue: string;
}) => {
  const detailModalState = useModalState<{
    alias: string | null;
  }>({
    alias: null,
  });
  const brandMap = useProductStore((state) => state.brandMap);
  const lineMap = useProductStore((state) => state.lineMap);
  const { productList, checkedList, searchValue, setCheckedList } = props;

  const ProductTableColumns: TableColumnT<ProductT>[] = [
    {
      columnKey: 'product',
      Header: 'Product',
      Cell: (rowData: RowData<ProductT>) => (
        <span className="truncate">{rowData.rowData.product_name}</span>
      ),
      className: 'flex-1 min-w-0',
      sortable: true,
      sort: (a, b) => {
        return a.product_name?.localeCompare(b.product_name || '') || 0;
      },
    },
    {
      columnKey: 'brand',
      Header: 'Brand',
      Cell: (rowData: RowData<ProductT>) => (
        <Badge>
          {rowData.rowData.brand_id
            ? brandMap.get(rowData.rowData.brand_id)?.name || '-'
            : '-'}
        </Badge>
      ),
      className: 'w-40',
      sortable: true,
      sort: (a, b) => {
        const aBrand = brandMap.get(a.brand_id || '');
        const bBrand = brandMap.get(b.brand_id || '');
        return aBrand?.name?.localeCompare(bBrand?.name || '') || 0;
      },
    },
    {
      columnKey: 'line',
      Header: 'Line',
      Cell: (rowData: RowData<ProductT>) => (
        <Badge>
          {rowData.rowData.line_id
            ? lineMap.get(rowData.rowData.line_id)?.name || '-'
            : '-'}
        </Badge>
      ),
      className: 'w-40',
      sortable: true,
      sort: (a, b) => {
        const aLine = lineMap.get(a.line_id || '');
        const bLine = lineMap.get(b.line_id || '');
        return aLine?.name?.localeCompare(bLine?.name || '') || 0;
      },
    },
    {
      columnKey: 'option',
      Header: 'Option',
      Cell: (rowData: RowData<ProductT>) => (
        <EllipsisCell
          dataList={
            rowData.rowData.options?.map((option) => ({
              id: option.id,
              name: option.option_name,
            })) || []
          }
          columnWidth={320}
        />
      ),
      className: 'w-80',
    },
    {
      columnKey: 'action',
      Header: 'Action',
      Cell: (rowData: RowData<ProductT>) => (
        <div className="flex justify-center w-full">
          <ProductManagerEllipsisMenu
            target="product"
            targetId={rowData.rowData.alias}
          />
        </div>
      ),
      className: 'w-20',
    },
  ];
  return (
    <>
      <BaseTable
        defaultSortKey="line"
        columns={ProductTableColumns}
        data={productList.map((product) => ({
          rowKey: product.alias,
          rowData: product,
        }))}
        searchValue={searchValue}
        onRowClick={(rowKey) =>
          detailModalState.open({ modalState: { alias: rowKey } })
        }
        placeholder={{
          title: 'No product yet',
        }}
        useCheckbox
        checkedList={checkedList}
        setCheckedList={setCheckedList}
      />
      <Modal
        status={detailModalState.status}
        closeModal={detailModalState.close}
      >
        {detailModalState.modalState?.alias && (
          <DetailModal
            alias={detailModalState.modalState.alias}
            type="product"
          />
        )}
      </Modal>
    </>
  );
};
